import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import ChangePasswordModal from '../ChangePasswordModal/ChangePasswordModal';
import './Sidebar.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  // Fonction pour récupérer le nombre de feedbacks non lus
  const fetchUnreadCount = async () => {
    try {
      const db = getFirestore();
      const feedbacksRef = collection(db, 'feedbacks');
      // Modifier la requête pour inclure les feedbacks sans champ 'read' ou avec read=false
      const q = query(feedbacksRef, 
        where('read', 'in', [false, null])  // Ceci ne fonctionnera pas avec null
      );
      // On doit donc faire deux requêtes
      const unreadSnapshot = await getDocs(query(feedbacksRef, where('read', '==', false)));
      const allSnapshot = await getDocs(feedbacksRef);
      
      // Compter les feedbacks non lus et ceux sans champ 'read'
      const unreadCount = unreadSnapshot.size + 
        allSnapshot.docs.filter(doc => !doc.data().hasOwnProperty('read')).length;
      
      setUnreadCount(unreadCount);
    } catch (error) {
      console.error('Erreur lors de la récupération des feedbacks non lus:', error);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    // Rafraîchir toutes les minutes
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Récupérer l'email de l'utilisateur connecté
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && user.email) {
        setUserEmail(user.email);
      } else {
        setUserEmail('');
      }
    });

    return () => unsubscribe();
  }, []);

  // Fonction de rafraîchissement générale
  const handleRefresh = async (path) => {
    // Rafraîchir le compteur de feedbacks dans tous les cas
    await fetchUnreadCount();

    // Naviguer vers la page
    navigate(path);

    // Si on va sur la page feedback, attendre un peu pour laisser la page se monter
    if (path === '/feedback') {
      // Petit délai pour laisser la page se monter
      setTimeout(() => {
        // Forcer un rafraîchissement de la page feedback
        window.dispatchEvent(new CustomEvent('refreshFeedback'));
      }, 100);
    }
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <img src="/images/PANDA.png" alt="Panda Logo" />
        <h2>Vapo Smoke Today</h2>
      </div>

      <div className="menu-items">
        <button 
          className={`menu-item ${location.pathname === '/dashboard' ? 'active' : ''}`}
          onClick={() => handleRefresh('/dashboard')}
        >
          <i className="fas fa-home"></i>
          <span>Dashboard</span>
        </button>
        <button 
          className={`menu-item ${location.pathname === '/news' ? 'active' : ''}`}
          onClick={() => handleRefresh('/news')}
        >
          <i className="fas fa-newspaper"></i>
          <span>Nouveautés</span>
        </button>
        <button 
          className={`menu-item ${location.pathname === '/deals' ? 'active' : ''}`}
          onClick={() => handleRefresh('/deals')}
        >
          <i className="fas fa-tags"></i>
          <span>Bons Plans</span>
        </button>
        <button 
          className={`menu-item ${location.pathname === '/loyalty' ? 'active' : ''}`}
          onClick={() => handleRefresh('/loyalty')}
        >
          <i className="fas fa-gift"></i>
          <span>Avantages</span>
        </button>
        <button 
          className={`menu-item ${location.pathname === '/shops' ? 'active' : ''}`}
          onClick={() => handleRefresh('/shops')}
        >
          <i className="fas fa-store"></i>
          <span>Boutiques</span>
        </button>
        <button 
          className={`menu-item ${location.pathname === '/feedback' ? 'active' : ''}`}
          onClick={() => handleRefresh('/feedback')}
        >
          <i className="fas fa-comments"></i>
          <span>Feedback</span>
          {unreadCount > 0 && (
            <span className="notification-badge">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          className={`menu-item ${location.pathname === '/statistics' ? 'active' : ''}`}
          onClick={() => navigate('/statistics')}
        >
          <i className="fas fa-chart-line"></i>
          <span>Statistiques</span>
        </button>
      </div>

      <div className="sidebar-footer">
        {userEmail && (
          <div className="user-email">
            <i className="fas fa-envelope"></i>
            <span className="user-email-text">{userEmail}</span>
          </div>
        )}
        <button 
          className="change-password-btn" 
          onClick={() => setShowChangePasswordModal(true)}
        >
          <i className="fas fa-key"></i>
          <span>Changer le mot de passe</span>
        </button>
        <button className="logout-btn" onClick={handleLogout}>
          <i className="fas fa-sign-out-alt"></i>
          <span>Déconnexion</span>
        </button>
      </div>

      {showChangePasswordModal && (
        <ChangePasswordModal onClose={() => setShowChangePasswordModal(false)} />
      )}
    </div>
  );
};

export default Sidebar; 